import {RunningTimesOffPeriodsWithoutDetail} from '@domain/running-times'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmStructure, useStep} from '@hconnect/common/hooks'
import {useMainEquipmentByPxTrendTagSuggestion} from '@hconnect/common/services'
import {useStoppedMainEquipmentByPxTrendTagSuggestion} from '@hconnect/common/services/stoppedMainEquipment.services'
import {Stack, Typography, Button, Box, CircularProgress} from '@mui/material'
import {generateStoppageDtoDefaultData} from '@widgets/running-times'
import {StoppageCreate} from '@widgets/shift-event-details'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {DowntimeAnalyticsEventName, FEATURE_NAMESPACE} from '../consts'
import {useTrackDowntimeDashboardAnalytics} from '../hooks'
import {useDowntimePlantId} from '../providers'

import {AmendStoppagesVariantSwitch, StoppageSwitchVariant} from './AmendStoppagesVariantSwitch'
import {NoMoreStoppages} from './NoMoreStoppages'

type Props = {
  stoppages: RunningTimesOffPeriodsWithoutDetail[]
  onViewChange: (view: StoppageSwitchVariant) => void
  removeStoppage: (stoppage: RunningTimesOffPeriodsWithoutDetail) => void
  prefillMainEquipment?: boolean
}

export const AmendStoppagesNavigator = ({
  stoppages,
  onViewChange,
  removeStoppage,
  prefillMainEquipment
}: Props) => {
  const plantId = useDowntimePlantId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const axiosInstance = ApiClient.axiosInstance
  const trackDowntimeDashboardAnalytics = useTrackDowntimeDashboardAnalytics(plantId)

  const maxStep = stoppages.length
  const [
    currentStep,
    {canGoToNextStep, canGoToPrevStep, goToNextStep, goToPrevStep, reset: resetStep}
  ] = useStep(maxStep)
  const currentStoppageIndex = currentStep - 1
  const currentItem = stoppages.at(currentStoppageIndex)

  const handleGoToNextStep = useCallback(() => {
    goToNextStep()
    if (currentItem)
      trackDowntimeDashboardAnalytics({
        name: DowntimeAnalyticsEventName.UserSkipStoppageWithMissingData,
        data: {equipmentId: currentItem.equipment.id}
      })
  }, [currentItem, goToNextStep, trackDowntimeDashboardAnalytics])

  const {
    data: suggestedMainEquipment,
    isLoading,
    isSuccess
  } = useMainEquipmentByPxTrendTagSuggestion(
    {
      plantId,
      pxTrendTag: currentItem?.equipment.id || '',
      axiosInstance
    },
    {enabled: !!currentItem?.equipment.id && !!prefillMainEquipment}
  )
  const {data: suggestedStoppedMainEquipmentId, isLoading: isStoppedMainEquipmentLoading} =
    useStoppedMainEquipmentByPxTrendTagSuggestion(
      {
        plantId,
        pxTrendTag: currentItem?.equipment.id || '',
        axiosInstance
      },
      {enabled: !!currentItem?.equipment.id}
    )
  const {data: upmStructure, isLoading: isUpmStructureLoading} = useUpmStructure({
    plantId,
    includeEquipment: false,
    options: {enabled: !!currentItem?.equipment.id}
  })

  const handleSubmitSuccess = (currentItem: RunningTimesOffPeriodsWithoutDetail) => {
    trackDowntimeDashboardAnalytics({
      name: DowntimeAnalyticsEventName.UserAmendStoppage,
      data: {equipmentId: currentItem.equipment.id}
    })
    if (stoppages.length && currentStoppageIndex === stoppages.length - 1) {
      resetStep()
    }
    removeStoppage(currentItem)
  }

  const closeNavigator = () => {
    onViewChange(null)
  }

  const initialData = useMemo(() => {
    if (!currentItem) return {}

    return generateStoppageDtoDefaultData({
      runningTime: currentItem.runningTime,
      equipment: currentItem.equipment,
      suggestedMainEquipment,
      suggestedDepartmentUpmId: suggestedStoppedMainEquipmentId,
      structure: upmStructure
    })
  }, [currentItem, suggestedMainEquipment, suggestedStoppedMainEquipmentId, upmStructure])

  useEffect(() => {
    if (suggestedMainEquipment && isSuccess)
      trackDowntimeDashboardAnalytics({
        name: DowntimeAnalyticsEventName.UserOpenStoppageToAmendForm,
        data: {equipmentId: suggestedMainEquipment.id}
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedMainEquipment?.id])

  if (isLoading || isStoppedMainEquipmentLoading || isUpmStructureLoading)
    return <CircularProgress />

  if (!currentItem) {
    return <NoMoreStoppages onClose={closeNavigator} />
  }

  return (
    <Stack width="100%" sx={{height: '100%', overflowY: 'auto'}}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{t('newStoppagesBanner.label.amendStoppages')}</Typography>
        <AmendStoppagesVariantSwitch value="navigator" onChange={onViewChange} />
      </Stack>
      <Box>
        <StoppageCreate
          key={`${currentItem.equipment.id}-${currentItem.runningTime.beginTechnical}`}
          plantId={plantId}
          data-test-id="amend-stoppages-navigator"
          doClose={closeNavigator}
          initialData={initialData}
          onSuccessSubmit={() => handleSubmitSuccess(currentItem)}
          onError={(error) => {
            // If stoppage already exists, remove it from the list
            // 409 is the status code for conflict
            if (error.response?.status === 409) {
              removeStoppage(currentItem)
            }
          }}
          bodyWithPadding={false}
          renderFormFooterActions={({doSubmit, isSubmitDisabled, isDirty}) => (
            <NavigatorActions
              canGoToPrevStep={canGoToPrevStep}
              goToPrevStep={goToPrevStep}
              currentStep={currentStep}
              maxStep={maxStep}
              canGoToNextStep={canGoToNextStep}
              goToNextStep={handleGoToNextStep}
              handleSave={doSubmit}
              handleSaveAndNext={doSubmit}
              isDirty={isDirty}
              isSubmitDisabled={isSubmitDisabled}
            />
          )}
          withSalesImpact={false}
          withPriority
          withCategory
          eventTypeLabelPrefix={currentItem.equipment.text}
        />
      </Box>
    </Stack>
  )
}

type NavigatorActionsProps = {
  canGoToPrevStep: boolean
  goToPrevStep: () => void
  currentStep: number
  maxStep: number
  canGoToNextStep: boolean
  goToNextStep: () => void
  handleSave: () => void
  handleSaveAndNext: () => void
  isDirty: boolean
  isSubmitDisabled: boolean
}

const NavigatorActions: React.FC<NavigatorActionsProps> = ({
  canGoToPrevStep,
  goToPrevStep,
  currentStep,
  maxStep,
  isDirty,
  isSubmitDisabled,
  canGoToNextStep,
  handleSaveAndNext,
  handleSave,
  goToNextStep
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap>
      {canGoToPrevStep ? (
        <Button onClick={goToPrevStep} variant="text">
          {t('button.previous')}
        </Button>
      ) : (
        <span style={{minWidth: 60}} />
      )}
      <Typography color="text.secondary">
        <span data-test-id="navigator-current-step-number">{currentStep}</span>
        {' / '}
        <span data-test-id="navigator-max-step-number">{maxStep}</span>
      </Typography>
      <NextButton
        isDirty={isDirty}
        isSubmitDisabled={isSubmitDisabled}
        canGoToNextStep={canGoToNextStep}
        handleSaveAndNext={handleSaveAndNext}
        handleSave={handleSave}
        goToNextStep={goToNextStep}
      />
    </Stack>
  )
}

type NextButtonProps = {
  isDirty: boolean
  isSubmitDisabled: boolean
  canGoToNextStep: boolean
  handleSaveAndNext: () => void
  handleSave: () => void
  goToNextStep: () => void
}

const NextButton: React.FC<NextButtonProps> = ({
  isDirty,
  isSubmitDisabled,
  canGoToNextStep,
  handleSaveAndNext,
  handleSave,
  goToNextStep
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  if (isDirty && canGoToNextStep) {
    return (
      <Button disabled={isSubmitDisabled} onClick={handleSaveAndNext} variant="text">
        {`${t('button.save')} & ${t('button.next')}`}
      </Button>
    )
  }

  if (isDirty) {
    return (
      <Button disabled={isSubmitDisabled} onClick={handleSave} variant="text">
        {t('button.save')}
      </Button>
    )
  }

  if (canGoToNextStep) {
    return (
      <Button onClick={goToNextStep} variant="text">
        {t('button.skip')}
      </Button>
    )
  }

  return <span style={{minWidth: 60}} />
}
